import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MdHandshake,
  MdOutlineRocketLaunch,
  MdPeople,
  MdTrendingUp,
  MdWbIncandescent,
} from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import {
  deleteSnapshot,
  getSnapshot,
  getSnapshotOverlaps,
  uploadPartnerSnapshot,
  type SnapshotDetail,
  type SnapshotOverlap,
} from 'services/snapshotsService';
import { EcosystemPlaybookCard } from 'views/admin/home/components/EcosystemPlaybookCard';
import { SnapshotCsvMappingFlow } from './components/SnapshotCsvMappingFlow';
import { SnapshotCsvUpload } from './components/SnapshotCsvUpload';
import { getSnapshotApiErrorMessage } from './snapshotApiError';
import { SnapshotDeleteModal } from './components/SnapshotDeleteModal';
import {
  SnapshotBackLink,
  SnapshotCard,
  SnapshotPageShell,
  SnapshotPageSpinner,
  SnapshotPrimaryButton,
  SnapshotStatusTag,
  SnapshotTableColumnHeader,
  useSnapshotTableStyles,
} from './components/snapshotUi';
import {
  canOpenSnapshotDetail,
  formatSnapshotLongDate,
  snapshotOverlapRates,
  statusColors,
  statusLabel,
} from './snapshotUtils';
import {
  getMockSnapshotOverlaps,
  MOCK_SNAPSHOT_DETAIL,
  MOCK_SNAPSHOT_ID,
} from './snapshotMockData';

const PLAYBOOKS = [
  {
    key: 'upsells',
    title: 'Upsells & Expansion',
    description:
      'Unlock revenue by expanding or upselling into new teams, departments, or geographies within your existing accounts.',
    icon: MdTrendingUp,
  },
  {
    key: 'accelerate',
    title: 'Accelerate Opportunities',
    description:
      "Use the other company's knowledge and relationships to speed up deals you have open.",
    icon: MdOutlineRocketLaunch,
  },
  {
    key: 'generate',
    title: 'Generate Opportunities',
    description:
      'Identify new pipeline by combining your leads with partner customer and opportunity relationships.',
    icon: MdWbIncandescent,
  },
  {
    key: 'partnerships',
    title: 'Build Partnerships',
    description:
      'Strengthen strategic partnerships through proactive support of expansion and deal acceleration.',
    icon: MdHandshake,
  },
  {
    key: 'leadToLead',
    title: 'Lead to Lead',
    description:
      'Coordinate with your partner on mutual leads and prospects so you avoid duplicate outreach and align on next steps.',
    icon: MdPeople,
  },
] as const;

function companyInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function formatCount(value: number) {
  return value.toLocaleString();
}

function lifecycleColors(stage: string) {
  const key = stage.toLowerCase();
  if (key.includes('customer')) return { bg: 'green.100', color: 'green.600' };
  if (key.includes('opportunit')) return { bg: 'purple.100', color: 'purple.600' };
  if (key.includes('lead')) return { bg: 'yellow.100', color: 'yellow.700' };
  return { bg: 'gray.100', color: 'gray.500' };
}

function CompanyMark({
  name,
  accent,
  listRole,
}: {
  name: string;
  accent: 'brand' | 'purple';
  listRole: 'yours' | 'theirs';
}) {
  const bg = accent === 'brand' ? 'brand.50' : 'purple.50';
  const color = accent === 'brand' ? 'brand.500' : 'purple.500';
  const textColor = useColorModeValue('navy.700', 'white');
  return (
    <Flex align="center" gap={3} minW={0}>
      <Flex
        align="center"
        justify="center"
        w="40px"
        h="40px"
        borderRadius="12px"
        bg={bg}
        color={color}
        fontSize="sm"
        fontWeight="700"
        flexShrink={0}
      >
        {companyInitials(name)}
      </Flex>
      <Box minW={0}>
        <Text fontSize="md" fontWeight="700" color={textColor} noOfLines={1}>
          {name}
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="500">
          {listRole === 'yours' ? 'Your list' : 'Their list'}
        </Text>
      </Box>
    </Flex>
  );
}

function SideColumn({
  stage,
  owner,
}: {
  stage: string;
  owner?: string | null;
}) {
  const colors = lifecycleColors(stage);
  return (
    <Flex direction="column" align="flex-start" gap={1}>
      <SnapshotStatusTag
        label={(stage || 'Unknown').toUpperCase()}
        bg={colors.bg}
        color={colors.color}
      />
      {owner && (
        <Text fontSize="sm" color="gray.500">
          {owner}
        </Text>
      )}
    </Flex>
  );
}

export function SnapshotDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth() || {};
  const navigate = useNavigate();
  const toast = useToast();
  const [snapshot, setSnapshot] = useState<SnapshotDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [partnerFile, setPartnerFile] = useState<File | null>(null);
  const [partnerUploadStep, setPartnerUploadStep] = useState<'upload' | 'mapping'>(
    'upload',
  );
  const [uploading, setUploading] = useState(false);
  const [activePlaybook, setActivePlaybook] = useState<string | null>(null);
  const [overlaps, setOverlaps] = useState<SnapshotOverlap[]>([]);
  const [loadingOverlaps, setLoadingOverlaps] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const playbookModal = useDisclosure();
  const deleteModal = useDisclosure();
  const textColor = useColorModeValue('navy.700', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const { borderColor, cellProps, rowHoverBg } = useSnapshotTableStyles();

  const isPreview = id === MOCK_SNAPSHOT_ID;

  const load = useCallback(async () => {
    if (!id) return;
    if (id === MOCK_SNAPSHOT_ID) {
      setSnapshot(MOCK_SNAPSHOT_DETAIL);
      setLoading(false);
      return;
    }
    if (!token) return;
    setLoading(true);
    try {
      const data = await getSnapshot(token, id);
      const canUpload = data.status === 'waiting_on_you';
      const canView = canOpenSnapshotDetail(data.status);
      if (!canView && !canUpload) {
        navigate('/admin/snapshots', { replace: true });
        return;
      }
      setSnapshot(data);
    } catch {
      toast({ title: 'Snapshot not found', status: 'error' });
      navigate('/admin/snapshots');
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openPlaybook = async (key: string) => {
    if (!id) return;
    setActivePlaybook(key);
    setOverlaps([]);
    playbookModal.onOpen();
    if (isPreview) {
      setOverlaps(getMockSnapshotOverlaps(key));
      return;
    }
    if (!token) return;
    setLoadingOverlaps(true);
    try {
      const rows = await getSnapshotOverlaps(token, id, key);
      setOverlaps(rows);
    } catch {
      toast({ title: 'Could not load overlaps', status: 'error' });
    } finally {
      setLoadingOverlaps(false);
    }
  };

  const handlePartnerUpload = async (mappings: {
    columnMapping: Record<string, string>;
    stageMapping: Record<string, 'lead' | 'opportunity' | 'customer'>;
  }) => {
    if (!token || !id || !partnerFile) return;
    setUploading(true);
    try {
      await uploadPartnerSnapshot(token, id, partnerFile, mappings);
      toast({
        title: 'Upload received',
        description: 'We are computing overlap. Check the snapshots list shortly.',
        status: 'success',
      });
      navigate('/admin/snapshots');
    } catch (err: unknown) {
      toast({
        title: getSnapshotApiErrorMessage(err, 'Upload failed'),
        status: 'error',
      });
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (isPreview) {
      deleteModal.onClose();
      toast({
        title: 'Preview only',
        description: 'This page uses sample data for design review.',
        status: 'info',
      });
      return;
    }
    if (!token || !id) return;
    setDeleting(true);
    try {
      const result = await deleteSnapshot(token, id);
      if (result.fullSnapshotRemoved) {
        toast({ title: 'Snapshot removed', status: 'success' });
        navigate('/admin/snapshots');
        return;
      }
      toast({
        title: 'Your list was removed',
        description: result.resultsRetained
          ? 'Overlap results are still available until they expire.'
          : undefined,
        status: 'success',
      });
      if (result.resultsRetained) {
        await load();
      } else {
        navigate('/admin/snapshots');
      }
    } catch {
      toast({ title: 'Could not remove your data', status: 'error' });
    } finally {
      setDeleting(false);
      deleteModal.onClose();
    }
  };

  if (loading || !snapshot) {
    return (
      <SnapshotPageShell>
        <SnapshotPageSpinner />
      </SnapshotPageShell>
    );
  }

  const isReady =
    snapshot.status === 'ready' || snapshot.status === 'expiring_soon';
  const needsUpload = snapshot.status === 'waiting_on_you';
  const colors = statusColors(snapshot.status);
  const activePlaybookMeta = PLAYBOOKS.find((p) => p.key === activePlaybook);
  const viewerIsInitiator = snapshot.isInitiator !== false;
  const overlapRates = snapshot.counts
    ? snapshotOverlapRates(snapshot.counts)
    : null;
  const viewerOverlapRate =
    snapshot.counts && overlapRates
      ? viewerIsInitiator
        ? overlapRates.initiatorRate
        : overlapRates.partnerRate
      : null;
  const yourListCount = snapshot.counts
    ? viewerIsInitiator
      ? snapshot.counts.initiatorCount
      : snapshot.counts.partnerCount
    : 0;
  const theirListCount = snapshot.counts
    ? viewerIsInitiator
      ? snapshot.counts.partnerCount
      : snapshot.counts.initiatorCount
    : 0;
  const yourCompanyName = viewerIsInitiator
    ? snapshot.initiatorCompanyName
    : snapshot.partnerCompanyName;
  const theirCompanyName = viewerIsInitiator
    ? snapshot.partnerCompanyName
    : snapshot.initiatorCompanyName;

  return (
    <SnapshotPageShell>
      <SnapshotBackLink mb={1} />

      {snapshot.yourDataRemoved && isReady && (
        <Alert status="info" borderRadius="16px" mb={6}>
          <AlertIcon />
          <AlertDescription>
            You removed your account list from this snapshot. Overlap results stay
            available until {formatSnapshotLongDate(snapshot.expiresAt)}.
            {snapshot.otherPartyDataRemoved
              ? ` ${
                  snapshot.isInitiator === false
                    ? snapshot.initiatorCompanyName
                    : snapshot.partnerCompanyName
                } has also removed their list.`
              : ''}
          </AlertDescription>
        </Alert>
      )}

      {isPreview && (
        <Alert status="info" borderRadius="16px" mb={6}>
          <AlertIcon />
          <AlertDescription>
            Design preview with sample data. Open a playbook to review the accounts
            table.
          </AlertDescription>
        </Alert>
      )}

      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={8}
      >
        <Box minW={0}>
          <Flex align="center" gap={3} mb={3} flexWrap="wrap">
            <SnapshotStatusTag
              label={statusLabel(snapshot.status, snapshot.partnerCompanyName)}
              bg={colors.bg}
              color={colors.color}
            />
            {snapshot.readyAt && (
              <Text fontSize="sm" color={muted}>
                Taken {formatSnapshotLongDate(snapshot.readyAt)}
                {snapshot.expiresAt
                  ? ` · Expires ${formatSnapshotLongDate(snapshot.expiresAt)}`
                  : ''}
              </Text>
            )}
          </Flex>
          <Flex align="center" gap={{ base: 3, md: 5 }} flexWrap="wrap">
            <CompanyMark
              name={snapshot.initiatorCompanyName}
              accent={viewerIsInitiator ? 'brand' : 'purple'}
              listRole={viewerIsInitiator ? 'yours' : 'theirs'}
            />
            <Flex
              align="center"
              justify="center"
              w="28px"
              h="28px"
              borderRadius="full"
              bg="gray.100"
              color="gray.500"
              fontSize="xs"
              fontWeight="700"
              flexShrink={0}
            >
              ×
            </Flex>
            <CompanyMark
              name={snapshot.partnerCompanyName}
              accent={viewerIsInitiator ? 'purple' : 'brand'}
              listRole={viewerIsInitiator ? 'theirs' : 'yours'}
            />
          </Flex>
          {!snapshot.yourDataRemoved && (
            <Button
              variant="ghost"
              size="sm"
              color={muted}
              fontWeight="500"
              mt={4}
              px={0}
              height="auto"
              onClick={deleteModal.onOpen}
            >
              Remove my list
            </Button>
          )}
        </Box>
      </Flex>

      {snapshot.status === 'processing' && (
        <SnapshotCard mb={8} p={6}>
          <Flex align="center" gap={3}>
            <Spinner size="sm" color="brand.400" thickness="3px" />
            <Box>
              <Text color={textColor} fontWeight="600">
                Computing overlap
              </Text>
              <Text fontSize="sm" color={muted} mt={1}>
                This usually takes a few minutes. You can leave and come back from
                the snapshots list.
              </Text>
            </Box>
          </Flex>
        </SnapshotCard>
      )}

      {needsUpload && (
        <SnapshotCard mb={8}>
          <Text fontSize="xl" fontWeight="700" color={textColor} mb={1}>
            Upload your account list
          </Text>
          <Text fontSize="sm" color={muted} mb={5} lineHeight="1.6">
            {snapshot.uploadPrompt
              ? snapshot.uploadPrompt
              : `${snapshot.initiatorCompanyName} invited you to share a list so you can see shared accounts.`}
          </Text>
          {partnerUploadStep === 'mapping' && partnerFile && token ? (
            <SnapshotCsvMappingFlow
              token={token}
              file={partnerFile}
              submitLabel="Submit list"
              isSubmitting={uploading}
              onBack={() => setPartnerUploadStep('upload')}
              onSubmit={handlePartnerUpload}
            />
          ) : (
            <>
              <SnapshotCsvUpload
                file={partnerFile}
                onFileChange={(f) => {
                  setPartnerFile(f);
                  setPartnerUploadStep('upload');
                }}
              />
              <Flex justify="flex-end" mt={6}>
                <SnapshotPrimaryButton
                  isDisabled={!partnerFile}
                  onClick={() => partnerFile && setPartnerUploadStep('mapping')}
                >
                  Continue
                </SnapshotPrimaryButton>
              </Flex>
            </>
          )}
        </SnapshotCard>
      )}

      {isReady && snapshot.counts && (
        <>
          <SnapshotCard mb={8} p={{ base: 6, md: 8 }}>
            <Flex gap={6} wrap="wrap">
              <Box flex="1" minW="140px">
                <Text fontSize="sm" color={muted} mb={1}>
                  Shared accounts
                </Text>
                <Text fontSize="2xl" fontWeight="700" color={textColor}>
                  {formatCount(snapshot.counts.overlapping)}
                </Text>
              </Box>
              {viewerOverlapRate !== null && (
                <Box flex="1" minW="140px">
                  <Text fontSize="sm" color={muted} mb={1}>
                    Of your list
                  </Text>
                  <Text fontSize="2xl" fontWeight="700" color={textColor}>
                    {viewerOverlapRate}%
                  </Text>
                </Box>
              )}
              <Box flex="1" minW="140px">
                <Text fontSize="sm" color={muted} mb={1}>
                  {yourCompanyName}
                </Text>
                <Text fontSize="2xl" fontWeight="700" color={textColor}>
                  {formatCount(yourListCount)}
                </Text>
                <Text fontSize="sm" color={muted} mt={1}>
                  accounts in your list
                </Text>
              </Box>
              <Box flex="1" minW="140px">
                <Text fontSize="sm" color={muted} mb={1}>
                  {theirCompanyName}
                </Text>
                <Text fontSize="2xl" fontWeight="700" color={textColor}>
                  {formatCount(theirListCount)}
                </Text>
                <Text fontSize="sm" color={muted} mt={1}>
                  accounts in their list
                </Text>
              </Box>
            </Flex>
          </SnapshotCard>

          <SnapshotCard p={{ base: 6, md: 8 }}>
            <Text fontSize="2xl" fontWeight="700" color={textColor} mb={1}>
              Where to act
            </Text>
            <Text color={muted} mb={6} maxW="640px">
              Each playbook groups overlapping accounts by the motion you can run
              together. Open one to review the matched companies.
            </Text>
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
              {PLAYBOOKS.map((pb) => (
                <EcosystemPlaybookCard
                  key={pb.key}
                  title={pb.title}
                  description={pb.description}
                  count={snapshot.playbooks?.[pb.key] ?? 0}
                  url="snapshot"
                  icon={pb.icon}
                  ctaLabel="View accounts"
                  onView={() => openPlaybook(pb.key)}
                />
              ))}
            </Grid>
          </SnapshotCard>
        </>
      )}

      {!isReady && !needsUpload && snapshot.status === 'waiting_on_partner' && (
        <SnapshotCard p={8}>
          <Text fontSize="xl" fontWeight="700" color={textColor} mb={2}>
            Waiting on {snapshot.partnerCompanyName}
          </Text>
          <Text color={muted} lineHeight="1.65" maxW="560px">
            We emailed {snapshot.partnerEmail || 'your partner'} to upload their
            account list. Overlap results will appear here once both lists are in.
          </Text>
        </SnapshotCard>
      )}

      <SnapshotDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        onConfirm={confirmDelete}
        isDeleting={deleting}
        yourCompanyName={
          snapshot.isInitiator === false
            ? snapshot.partnerCompanyName
            : snapshot.initiatorCompanyName
        }
        otherCompanyName={
          snapshot.isInitiator === false
            ? snapshot.initiatorCompanyName
            : snapshot.partnerCompanyName
        }
        hasResults={
          snapshot.status === 'ready' ||
          snapshot.status === 'expiring_soon' ||
          snapshot.status === 'processing'
        }
      />

      <Modal isOpen={playbookModal.isOpen} onClose={playbookModal.onClose} size="4xl">
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="30px" mx={4}>
          <ModalHeader pr={12} pb={2}>
            <Text fontSize="xl" fontWeight="700">
              {activePlaybookMeta?.title || 'Overlaps'}
            </Text>
            {activePlaybookMeta && (
              <Text fontSize="sm" fontWeight="400" color={muted} mt={1} lineHeight="1.5">
                {activePlaybookMeta.description}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8} pt={4}>
            {loadingOverlaps ? (
              <SnapshotPageSpinner />
            ) : overlaps.length === 0 ? (
              <Flex direction="column" align="center" py={12} textAlign="center">
                <Text fontWeight="600" color={textColor} mb={1}>
                  No accounts in this playbook
                </Text>
                <Text fontSize="sm" color={muted} maxW="360px">
                  Try another playbook to see overlapping companies from this
                  snapshot.
                </Text>
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Text fontSize="sm" color={muted} mb={4}>
                  {overlaps.length} overlapping{' '}
                  {overlaps.length === 1 ? 'account' : 'accounts'}
                </Text>
                <Table variant="simple" color="gray.500">
                  <Thead>
                    <Tr>
                      <Th pe="10px" borderColor={borderColor}>
                        <SnapshotTableColumnHeader>ACCOUNT</SnapshotTableColumnHeader>
                      </Th>
                      <Th pe="10px" borderColor={borderColor}>
                        <SnapshotTableColumnHeader>
                          {snapshot.initiatorCompanyName.toUpperCase()}
                        </SnapshotTableColumnHeader>
                      </Th>
                      <Th pe="10px" borderColor={borderColor}>
                        <SnapshotTableColumnHeader>
                          {snapshot.partnerCompanyName.toUpperCase()}
                        </SnapshotTableColumnHeader>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {overlaps.map((row) => (
                      <Tr key={row.domain} _hover={{ bg: rowHoverBg }} transition="all 0.2s">
                        <Td {...cellProps}>
                          <Text fontWeight="600" color={textColor}>
                            {row.initiator.company || row.domain}
                          </Text>
                          <Text fontSize="sm" color={muted}>
                            {row.domain}
                          </Text>
                        </Td>
                        <Td {...cellProps}>
                          <SideColumn
                            stage={row.initiator.lifecycleStage}
                            owner={row.initiator.accountOwner}
                          />
                        </Td>
                        <Td {...cellProps}>
                          <SideColumn
                            stage={row.partner.lifecycleStage}
                            owner={row.partner.accountOwner}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </SnapshotPageShell>
  );
}
